/**
 * Service for generating document outlines and inserting sections
 * Note: Boilerplate content is inserted during regeneration, not outline creation
 */

import { sectionHeaders } from '../components/editors/spec/blocks/blockDefinitions.js';

export class OutlineGenerationService {
    constructor() {
        this.regenerativeSections = new Set(['title', 'field', 'background', 'summary', 'embodiments', 'detailed']);
    }

    /**
     * Creates a complete document outline with selected sections
     * @param {Object} editor - The TipTap editor instance
     * @param {Array} selectedSections - Array of section IDs to include
     * @returns {Object} Result with sections created and regenerative blocks added
     */
    createOutline(editor, selectedSections) {
        if (!editor) {
            console.error('OutlineGenerationService: No editor provided');
            return { success: false, error: 'No editor provided' };
        }

        const results = {
            sectionsCreated: [],
            regenerativeBlocksAdded: [],
            existingSections: [],
            errors: []
        };

        try {
            // Get section details for selected sections
            const sectionsToCreate = sectionHeaders.filter(s => selectedSections.includes(s.id));
            
            // Sort sections by their original order in sectionHeaders
            sectionsToCreate.sort((a, b) => {
                const indexA = sectionHeaders.findIndex(s => s.id === a.id);
                const indexB = sectionHeaders.findIndex(s => s.id === b.id);
                return indexA - indexB;
            });

            // Build all content at once
            const { content, metadata } = this.buildOutlineContent(editor, sectionsToCreate);
            
            // Insert all content in a single transaction
            if (content.length > 0) {
                // Find insertion position (end of document or after existing content)
                const { doc } = editor.state;
                let insertPos = doc.content.size;
                
                // If document has only empty paragraphs at the end, find a better position
                if (doc.lastChild && doc.lastChild.type.name === 'paragraph' && doc.lastChild.content.size === 0) {
                    insertPos = doc.content.size - doc.lastChild.nodeSize;
                }
                
                // Insert all content at once
                editor.chain()
                    .focus()
                    .insertContentAt(insertPos, content)
                    .run();
                
                // Update results based on metadata
                results.sectionsCreated = metadata.created;
                results.regenerativeBlocksAdded = metadata.regenerativeBlocks;
                results.existingSections = metadata.existing;
            }
            
        } catch (error) {
            console.error('Error creating outline:', error);
            results.errors.push({ error: error.message });
        }

        return {
            success: results.errors.length === 0,
            ...results
        };
    }

    /**
     * Build all content to be inserted
     * @param {Object} editor - The TipTap editor instance
     * @param {Array} sectionsToCreate - Array of section configurations
     * @returns {Object} Content array and metadata
     */
    buildOutlineContent(editor, sectionsToCreate) {
        const { doc, schema } = editor.state;
        const content = [];
        const metadata = {
            created: [],
            existing: [],
            regenerativeBlocks: []
        };

        // Check which sections already exist
        const existingSections = new Set();
        doc.descendants((node) => {
            if (node.type.name === 'heading' && node.attrs.sectionType) {
                existingSections.add(node.attrs.sectionType);
            }
        });

        // Check if title already exists (new format: level 2 with sectionType: 'title')
        let titleExists = existingSections.has('title');

        // Build content for each section
        sectionsToCreate.forEach(section => {
            // Special handling for title section
            if (section.id === 'title') {
                if (!titleExists) {
                    // Create title section header with "Title" placeholder
                    const titleHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'title',
                        class: 'section-title'
                    }, schema.text('Title'));

                    content.push(titleHeaderNode.toJSON());

                    metadata.created.push('title');
                } else {
                    metadata.existing.push('title');
                }
                return; // Skip the rest of the processing for title
            }

            // Special handling for field section
            if (section.id === 'field') {
                if (!existingSections.has('field')) {
                    // Create field section header
                    const fieldHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'field',
                        class: 'section-field'
                    }, schema.text('FIELD'));

                    content.push(fieldHeaderNode.toJSON());

                    // Create regenerative block for the field content
                    const fieldContentBlock = {
                        type: 'paragraph',
                        attrs: {
                            class: 'regenerative-support field-content',
                            blockType: 'regenerative-support',
                            context: 'field'
                        }
                    };
                    content.push(fieldContentBlock);

                    metadata.created.push('field');
                } else {
                    metadata.existing.push('field');

                    // Check if we need to add regenerative block to existing field section
                    const hasRegenerativeBlock = this.sectionHasRegenerativeBlock(editor, 'field');
                    if (!hasRegenerativeBlock) {
                        // Add regenerative block to existing field section
                        this.addRegenerativeBlockToExistingSection(editor, 'field');
                        metadata.regenerativeBlocks.push('field');
                    }
                }
                return; // Skip the rest of the processing for field
            }

            // Special handling for incorporation section
            // Note: Boilerplate text is added during regeneration
            if (section.id === 'incorporation') {
                if (!existingSections.has('incorporation')) {
                    const incorporationHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'incorporation',
                        class: 'section-incorporation'
                    }, schema.text('Incorporation by Reference'));
                    content.push(incorporationHeaderNode.toJSON());
                    content.push({ type: 'paragraph' });
                    metadata.created.push('incorporation');
                } else {
                    metadata.existing.push('incorporation');
                }
                return;
            }

            // Special handling for drawings section
            // Note: Intro text and boilerplate are added during regeneration
            if (section.id === 'drawings') {
                if (!existingSections.has('drawings')) {
                    const drawingsHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'drawings',
                        class: 'section-drawings'
                    }, schema.text('Brief Description of Drawings'));
                    content.push(drawingsHeaderNode.toJSON());
                    content.push({ type: 'paragraph' });
                    metadata.created.push('drawings');
                } else {
                    metadata.existing.push('drawings');
                }
                return;
            }

            // Special handling for detailed section
            // Note: Definitions subsection is added during regeneration (after 4th paragraph)
            if (section.id === 'detailed') {
                if (!existingSections.has('detailed')) {
                    const detailedHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'detailed',
                        class: 'section-detailed'
                    }, schema.text('Detailed Description'));
                    content.push(detailedHeaderNode.toJSON());

                    // Add regenerative block for detailed description content
                    const detailedRegenerativeBlock = {
                        type: 'paragraph',
                        attrs: {
                            class: 'regenerative-support',
                            blockType: 'regenerative-support',
                            context: 'detailed-description'
                        }
                    };
                    content.push(detailedRegenerativeBlock);
                    metadata.regenerativeBlocks.push('detailed-description');
                    metadata.created.push('detailed');
                } else {
                    metadata.existing.push('detailed');
                    const hasDetailedDescriptionBlock = this.sectionHasRegenerativeBlock(editor, 'detailed-description');
                    if (!hasDetailedDescriptionBlock) {
                        this.addRegenerativeBlockToExistingSection(editor, 'detailed', 'detailed-description');
                        metadata.regenerativeBlocks.push('detailed-description');
                    }
                }
                return;
            }

            // Special handling for embodiments section
            // Note: Specification Boilerplate is added during regeneration
            if (section.id === 'embodiments') {
                if (!existingSections.has('embodiments')) {
                    const embodimentsHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'embodiments',
                        class: 'section-embodiments'
                    }, schema.text('Exemplary Embodiments'));
                    content.push(embodimentsHeaderNode.toJSON());

                    const embodimentsRegenerativeBlock = {
                        type: 'paragraph',
                        attrs: {
                            class: 'regenerative-support',
                            blockType: 'regenerative-support',
                            context: 'embodiments'
                        }
                    };
                    content.push(embodimentsRegenerativeBlock);
                    metadata.regenerativeBlocks.push('embodiments');
                    metadata.created.push('embodiments');
                } else {
                    metadata.existing.push('embodiments');
                    const hasRegenerativeBlock = this.sectionHasRegenerativeBlock(editor, 'embodiments');
                    if (!hasRegenerativeBlock) {
                        this.addRegenerativeBlockToExistingSection(editor, 'embodiments');
                        metadata.regenerativeBlocks.push('embodiments');
                    }
                }
                return;
            }

            // Special handling for examples section
            // Note: Specification Boilerplate is added during regeneration
            if (section.id === 'examples') {
                if (!existingSections.has('examples')) {
                    const examplesHeaderNode = schema.nodes.heading.create({
                        level: 2,
                        sectionType: 'examples',
                        class: 'section-examples'
                    }, schema.text('Examples'));
                    content.push(examplesHeaderNode.toJSON());
                    content.push({ type: 'paragraph' });
                    metadata.created.push('examples');
                } else {
                    metadata.existing.push('examples');
                }
                return;
            }

            if (existingSections.has(section.id)) {
                metadata.existing.push(section.id);

                // Check if we need to add regenerative block to existing section
                if (this.regenerativeSections.has(section.id)) {
                    // For summary section, check for overview block
                    if (section.id === 'summary') {
                        const hasOverviewBlock = this.sectionHasRegenerativeBlock(editor, 'overview');
                        if (!hasOverviewBlock) {
                            this.addRegenerativeBlockToExistingSection(editor, section.id, 'overview');
                            metadata.regenerativeBlocks.push('overview');
                        }
                        const hasRegenerativeBlock = this.sectionHasRegenerativeBlock(editor, section.id);
                        if (!hasRegenerativeBlock) {
                            this.addRegenerativeBlockToExistingSection(editor, section.id);
                            metadata.regenerativeBlocks.push(section.id);
                        }
                    }
                    // For detailed section, ONLY add detailed-description block (not a regular 'detailed' block)
                    else if (section.id === 'detailed') {
                        const hasDetailedDescriptionBlock = this.sectionHasRegenerativeBlock(editor, 'detailed-description');
                        if (!hasDetailedDescriptionBlock) {
                            this.addRegenerativeBlockToExistingSection(editor, section.id, 'detailed-description');
                            metadata.regenerativeBlocks.push('detailed-description');
                        }
                    }
                    // For other sections, add regular regenerative block
                    else {
                        const hasRegenerativeBlock = this.sectionHasRegenerativeBlock(editor, section.id);
                        if (!hasRegenerativeBlock) {
                            this.addRegenerativeBlockToExistingSection(editor, section.id);
                            metadata.regenerativeBlocks.push(section.id);
                        }
                    }
                }
            } else {
                // Create section header
                const headingNode = schema.nodes.heading.create({
                    level: 2,
                    sectionType: section.id,
                    class: `section-${section.id}`
                }, schema.text(section.title));

                content.push(headingNode.toJSON());
                
                // Add regenerative block if this section supports it
                if (this.regenerativeSections.has(section.id)) {
                    // For summary section, add overview block first
                    if (section.id === 'summary') {
                        const overviewBlock = {
                            type: 'paragraph',
                            attrs: {
                                class: 'regenerative-support',
                                blockType: 'regenerative-support',
                                context: 'overview'
                            }
                        };
                        content.push(overviewBlock);
                        metadata.regenerativeBlocks.push('overview');
                    }

                    // For detailed section, ONLY add detailed-description block (not a regular 'detailed' block)
                    if (section.id === 'detailed') {
                        const detailedRegenerativeBlock = {
                            type: 'paragraph',
                            attrs: {
                                class: 'regenerative-support',
                                blockType: 'regenerative-support',
                                context: 'detailed-description'
                            }
                        };
                        content.push(detailedRegenerativeBlock);
                        metadata.regenerativeBlocks.push('detailed-description');
                    } else {
                        // Add regular regenerative block for other sections
                        const regenerativeBlock = {
                            type: 'paragraph',
                            attrs: {
                                class: 'regenerative-support',
                                blockType: 'regenerative-support',
                                context: section.id
                            }
                        };
                        content.push(regenerativeBlock);
                        metadata.regenerativeBlocks.push(section.id);
                    }
                } else {
                    // Add empty paragraph after heading for non-regenerative sections
                    content.push({ type: 'paragraph' });
                }
                
                metadata.created.push(section.id);
            }
        });

        return { content, metadata };
    }

    /**
     * Check if a section has a regenerative block
     * @param {Object} editor - The TipTap editor instance
     * @param {string} sectionId - The section ID
     * @returns {boolean} Whether the section has a regenerative block
     */
    sectionHasRegenerativeBlock(editor, sectionId) {
        const { doc } = editor.state;
        let hasBlock = false;
        
        doc.descendants((node) => {
            if (node.type.name === 'paragraph' && 
                node.attrs.blockType === 'regenerative-support' &&
                node.attrs.context === sectionId) {
                hasBlock = true;
                return false;
            }
        });
        
        return hasBlock;
    }

    /**
     * Add regenerative block to an existing section
     * @param {Object} editor - The TipTap editor instance
     * @param {string} sectionId - The section ID
     * @param {string} context - Optional context override (defaults to sectionId)
     */
    addRegenerativeBlockToExistingSection(editor, sectionId, context = null) {
        const { doc } = editor.state;
        let sectionHeadingPos = null;
        
        // Find the section heading
        doc.descendants((node, pos) => {
            if (node.type.name === 'heading' && node.attrs.sectionType === sectionId) {
                sectionHeadingPos = pos;
                return false;
            }
        });
        
        if (sectionHeadingPos !== null) {
            const regenerativeBlock = {
                type: 'paragraph',
                attrs: {
                    class: 'regenerative-support',
                    blockType: 'regenerative-support',
                    context: context || sectionId
                }
            };
            
            // Insert right after the heading
            const headingNode = doc.nodeAt(sectionHeadingPos);
            const insertPos = sectionHeadingPos + headingNode.nodeSize;
            
            editor.chain()
                .focus()
                .insertContentAt(insertPos, regenerativeBlock)
                .run();
        }
    }

    /**
     * Get sections that have regenerative blocks
     * @param {Object} editor - The TipTap editor instance
     * @returns {Set} Set of section IDs with regenerative blocks
     */
    getRegenerativeSections(editor) {
        const { doc } = editor.state;
        const sections = new Set();
        
        doc.descendants((node) => {
            if (node.type.name === 'paragraph' && 
                node.attrs.blockType === 'regenerative-support' &&
                node.attrs.context) {
                sections.add(node.attrs.context);
            }
        });
        
        return sections;
    }
}

export const outlineGenerationService = new OutlineGenerationService();